"use client"

import React, { useState, useEffect } from 'react';
import { NavigationProps } from "@/types/navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { 
  User, 
  Mail, 
  Lock, 
  Bell, 
  Shield, 
  Globe,
  Smartphone,
  CreditCard,
  Loader2
} from "lucide-react";
import { usePlaid } from '@/contexts/PlaidContext';
import { useToast } from "@/components/ui/use-toast";

interface AccountPageProps extends NavigationProps {}

export default function AccountPage({ onNavigate }: AccountPageProps) {
  const { currentUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const { bankAccounts, plaidItems, isLoading, disconnectAccount, refreshAccounts } = usePlaid();
  const [disconnectingAccounts, setDisconnectingAccounts] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  
  useEffect(() => {
    refreshAccounts();
  }, [refreshAccounts]);

  const handleDisconnect = async (plaidItemId: string, accountId: string) => {
    try {
      setDisconnectingAccounts(prev => ({ ...prev, [accountId]: true }));
      await disconnectAccount(plaidItemId, accountId);
      toast({
        title: "Success",
        description: "Account disconnected successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to disconnect account",
        variant: "destructive",
      });
    } finally {
      setDisconnectingAccounts(prev => ({ ...prev, [accountId]: false }));
    }
  };

  // Get initials for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  const displayName = currentUser?.displayName || "User";
  const email = currentUser?.email || "user@example.com";
  const photoURL = currentUser?.photoURL || "/avatars/default.jpg";
  const initials = getInitials(displayName);

  return (
    <>
      <PageHeader 
        onNavigate={onNavigate}
        currentPage="Account"
        showDashboardLink={true}
      />

      <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Account Settings</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Manage your account preferences and settings</p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Profile Overview Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-muted-foreground" />
                Profile Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={photoURL} alt={displayName} />
                  <AvatarFallback className="text-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-1 text-center sm:text-left">
                  <h3 className="font-semibold text-lg">{displayName}</h3>
                  <p className="text-sm text-muted-foreground">{email}</p>
                  <p className="text-sm text-muted-foreground">Pro Plan Member</p>
                </div>
                <Button variant="outline" onClick={() => setIsEditing(!isEditing)}>
                  {isEditing ? "Cancel" : "Edit Profile"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="general" className="space-y-4">
            <TabsList>
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
              <TabsTrigger value="preferences">Preferences</TabsTrigger>
              <TabsTrigger value="linked-accounts">Linked Accounts</TabsTrigger>
            </TabsList>

            {/* General Settings */}
            <TabsContent value="general">
              <Card>
                <CardHeader>
                  <CardTitle>General Information</CardTitle>
                  <CardDescription>Update your basic profile information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Display Name</Label>
                    <Input 
                      id="name" 
                      defaultValue={displayName}
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      defaultValue={email}
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Input 
                      id="timezone" 
                      defaultValue="Pacific Time (PT)"
                      disabled={!isEditing}
                    />
                  </div>
                </CardContent>
                <CardFooter>
                  <Button disabled={!isEditing}>Save Changes</Button>
                </CardFooter>
              </Card>
            </TabsContent>

            {/* Security Settings */}
            <TabsContent value="security">
              <Card>
                <CardHeader>
                  <CardTitle>Security Settings</CardTitle>
                  <CardDescription>Manage your account security preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Two-Factor Authentication</Label>
                      <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
                    </div>
                    <Switch />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Login Notifications</Label>
                      <p className="text-sm text-muted-foreground">Get notified about new login attempts</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <Button variant="outline" className="w-full" onClick={() => onNavigate('reset-password')}>
                    Change Password
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notification Preferences */}
            <TabsContent value="notifications">
              <Card>
                <CardHeader>
                  <CardTitle>Notification Settings</CardTitle>
                  <CardDescription>Choose what notifications you want to receive</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Email Notifications</Label>
                      <p className="text-sm text-muted-foreground">Receive updates and alerts via email</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Push Notifications</Label>
                      <p className="text-sm text-muted-foreground">Get notified on your device</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Budget Alerts</Label>
                      <p className="text-sm text-muted-foreground">Get notified when approaching budget limits</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* User Preferences */}
            <TabsContent value="preferences">
              <Card>
                <CardHeader>
                  <CardTitle>User Preferences</CardTitle>
                  <CardDescription>Customize your app experience</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Currency</Label>
                      <p className="text-sm text-muted-foreground">Choose your preferred currency</p>
                    </div>
                    <Input defaultValue="USD" className="w-24" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Language</Label>
                      <p className="text-sm text-muted-foreground">Select your preferred language</p>
                    </div>
                    <Input defaultValue="English" className="w-24" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Date Format</Label>
                      <p className="text-sm text-muted-foreground">Choose how dates are displayed</p>
                    </div>
                    <Input defaultValue="MM/DD/YYYY" className="w-24" />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Linked Accounts Settings */}
            <TabsContent value="linked-accounts">
              <Card>
                <CardHeader>
                  <CardTitle>Linked Bank Accounts</CardTitle>
                  <CardDescription>Manage your connected bank accounts via Plaid</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Remove PlaidLinkButton and bank accounts section */}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Quick Actions */}
          <div className="grid gap-4 md:grid-cols-3 mt-6">
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => onNavigate('billing')}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Manage Billing
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => onNavigate('help')}
            >
              <Globe className="mr-2 h-4 w-4" />
              Get Help
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => onNavigate('settings')}
            >
              <Shield className="mr-2 h-4 w-4" />
              Privacy Settings
            </Button>
          </div>
        </div>
      </main>
    </>
  );
} 